/** Reine Logik von Schritt 1 „Stelle“ (Anzeigenprüfung, Link-Erkennung, Namensvorschlag). */

import { AdCheckItem, AdCheckLevel, AdCheckResult } from '../../campaign-wizard.types';
import { wz } from '../../components/wizard-tokens';
import { AdSource, gateStatus, hasPlaceholders, LOCATION_LABEL, PLACEHOLDER } from './job-gate.logic';

export { hasPlaceholders };

export type AdStatus = 'idle' | 'checking' | 'done' | 'stale';

export type StoredAdCheck = (AdCheckResult & { checkedText: string; checkedHash?: string }) | null;

/** Zustand der Anzeigenprüfung aus gespeichertem Ergebnis und aktuellem Text. */
export const adStatus = (adCheck: StoredAdCheck, briefing: AdSource, checking: boolean): AdStatus => {
  if (checking) return 'checking';
  if (!adCheck) return 'idle';
  return gateStatus(adCheck, briefing) === 'checked' ? 'done' : 'stale';
};

export interface AdLevelMeta {
  titleKey: string | null;
  descKey: string | null;
  bg: string;
  fg: string;
  /** Name des Material-Icons. */
  icon: 'error_outline' | 'info' | 'check_circle' | 'edit_note';
}

/** Stufen der Anzeigenprüfung (Farben, Icons, Texte) exakt wie im Design (`LV`). */
export const adLevelMeta = (level: AdCheckLevel): AdLevelMeta => {
  switch (level) {
    case 1:
      return {
        titleKey: 'wizJobLevel1',
        descKey: 'wizJobLevel1Desc',
        bg: wz.redBg,
        fg: wz.redFg,
        icon: 'error_outline',
      };
    case 2:
      return {
        titleKey: 'wizJobLevel2',
        descKey: 'wizJobLevel2Desc',
        bg: wz.warnBg,
        fg: wz.warnFg,
        icon: 'info',
      };
    case 3:
      return {
        titleKey: 'wizJobLevel3',
        descKey: 'wizJobLevel3Desc',
        bg: wz.greenBg,
        fg: '#2B5A00',
        icon: 'check_circle',
      };
    default:
      return { titleKey: null, descKey: null, bg: wz.page, fg: wz.muted, icon: 'edit_note' };
  }
};

/** Punkte, die fehlen. */
export const missingItems = (items: AdCheckItem[]): AdCheckItem[] =>
  items.filter(i => !i.ok && i.label !== LOCATION_LABEL);

/** Antwort auf die Ortsfrage als Zeile fürs Ergänzungsfeld: „Ort: Köln“ bzw. „Ort: 100 % remote“. */
export const locationLine = (city: string, remote: boolean): string =>
  remote ? `${LOCATION_LABEL}: 100 % remote` : `${LOCATION_LABEL}: ${city.trim()}`;

/** Setzt die Ort-Zeile im Ergänzungsfeld (eine vorhandene „Ort:“-Zeile wird ersetzt, nie doppelt). */
export const setLocationLine = (extras: string, line: string): string => {
  const rest = extras
    .split(/\r?\n/)
    .filter(l => !new RegExp(`^\\s*${LOCATION_LABEL}\\s*:`, 'i').test(l))
    .join('\n')
    .replace(/\s+$/, '');
  return rest ? `${rest}\n${line}` : line;
};

/** Fehlende Punkte, für die es einen Ergänzungssatz gibt. */
export const fixableItems = (items: AdCheckItem[]): AdCheckItem[] =>
  missingItems(items).filter(i => i.add.trim().length > 0);

/** Hängt einen Ergänzungssatz an die Beschreibung an (wie `append` im Design). */
export const appendAddition = (briefing: string, add: string): string => briefing.trim() + add;

/** Alle Ergänzungen der fehlenden Punkte anhängen (wie `adFixAll`). */
export const appendAllAdditions = (briefing: string, items: AdCheckItem[]): string =>
  briefing.trim() +
  fixableItems(items)
    .map(i => i.add)
    .join('');

/** Jobtitel-Zeile aus den Ergänzungen („Jobtitel: …“): gehört zum Namensvorschlag. */
const suppliedJobTitle = (extras: string): string =>
  /^[ \t]*Jobtitel[ \t]*:[ \t]*([^\s[][^\n]*)$/im.exec(extras)?.[1]?.trim() ?? '';

/** Schlüssel, nach dem Schritt 1 Namen/Jobtitel neu ableitet (Anzeige + gelieferter Jobtitel der Ergänzungen). */
export const deriveTriggerKey = (briefing: string, extras: string): string =>
  `${briefing}\u0000${suppliedJobTitle(extras)}`;

/**
 * Schlüssel, mit dem Schritt 1 startet: Ist der geladene Stand schon abgeleitet (Name und Jobtitel vorhanden), wird beim bloßen
 * Öffnen nichts neu abgeleitet (C-4: keine KI-Kosten bei Zurück-Navigation/Neuladen); erst eine Änderung löst die Ableitung aus.
 */
export const initialDeriveKey = (s: { briefing: string; briefingExtras: string; name: string; jobTitle: string }): string =>
  s.briefing.trim() !== '' && s.name.trim() !== '' && s.jobTitle.trim() !== ''
    ? deriveTriggerKey(s.briefing, s.briefingExtras)
    : '';

/** Platzhalter, den jeder Leitsatz (Ergänzung ohne konkreten Inhalt) bekommt – damit der Platzhalter-Check greift. */
export const LEAD_PLACEHOLDER = '[…]';

/** Anweisung statt Fakt („Nenne mindestens ein konkretes Angebot wie …“): als Ergänzung wertlos, würde ungeprüft weiterlaufen. */
const INSTRUCTION_START =
  /^(?:bitte|hier|gerne|einfach|ggf\.?|gegebenenfalls|nenne|nennt|gib|beschreibe|erg(?:ä|ae)nze|f(?:ü|ue)ge|erw(?:ä|ae)hne|schreibe|benenne|formuliere|erkl(?:ä|ae)re|trage|definiere|liste|f(?:ü|ue)hre|lege|mindestens ein)\b/i;
/** Infinitiv-Imperativ am Satzende („… konkretes Benefit ergänzen“, „Ort hier eintragen“). */
const INSTRUCTION_END =
  /\b(?:erg(?:ä|ae)nzen|eintragen|angeben|nennen|benennen|beschreiben|hinzuf(?:ü|ue)gen|auflisten|erw(?:ä|ae)hnen|festlegen|spezifizieren|definieren|formulieren|ausf(?:ü|ue)llen)$/i;

/** Aufforderung statt Aussage (auch mit Leitsatz-Präfix „Punkt: …“). Identisch zum Backend (`isInstructionText`). */
export const isInstructionText = (raw: string): boolean => {
  const text = raw.trim().replace(/^[^:\n]{1,40}:\s*/, '');
  if (!text) return false;
  if (INSTRUCTION_START.test(text) || /\bbitte\b/i.test(text)) return true;
  const core = text.replace(/\([^)]*\)/g, ' ').replace(/[\s.!:;]+$/, '');
  return INSTRUCTION_END.test(core);
};

/** Hat die KI für diesen Punkt eine konkrete Ergänzung geliefert? Sonst gibt es nur den Leitsatz mit Platzhalter. */
export const hasConcreteAdd = (item: AdCheckItem): boolean =>
  item.add.trim().length > 0 && !isInstructionText(item.add);

/**
 * Was beim Klick ins Ergänzungsfeld kommt: die konkrete Ergänzung der KI, sonst der Leitsatz
 * „Sprache: […]“ (Name des Punktes + Platzhalter) – nie ein erfundener Wert, nie ohne Platzhalter.
 */
export const suggestionOf = (item: AdCheckItem): string =>
  hasConcreteAdd(item) ? item.add.trim() : `${item.label}: ${LEAD_PLACEHOLDER}`;

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Text vor dem ersten „[…]“-Platzhalter eines Vorschlags („Ort: “ aus „Ort: [Ort eintragen]“), sonst leer. */
const placeholderPrefix = (add: string): string => {
  const m = PLACEHOLDER.exec(add);
  return m ? add.slice(0, m.index).trim() : '';
};

const extrasLines = (extras: string): string[] => extras.split(/\r?\n/).map(l => l.trim());

/** Gibt es eine Zeile, die mit dem Präfix beginnt (egal ob schon ausgefüllt)? */
const hasLineWithPrefix = (extras: string, prefix: string): boolean =>
  prefix !== '' && extrasLines(extras).some(l => l.toLowerCase().startsWith(prefix.toLowerCase()));

/**
 * Wurde der Vorschlag dieses Punktes schon ins Ergänzungsfeld übernommen? (aus dem Text abgeleitet: überlebt Neuladen)
 *
 * Vorschläge mit „[…]“-Platzhalter gelten erst als ergänzt, wenn die Zeile mit gleichem Präfix existiert, der Platzhalter
 * ersetzt ist und etwas hinter dem Präfix steht – eine bloße Platzhalterzeile trägt keine Information (Gate/Backend werten sie als leer).
 */
export const isAddedToExtras = (extras: string, item: AdCheckItem): boolean => {
  if (hasConcreteAdd(item)) {
    const add = item.add.trim();
    if (!PLACEHOLDER.test(add)) return extras.includes(add);
    const prefix = placeholderPrefix(add);
    if (!prefix) return false;
    return extrasLines(extras).some(
      l => l.toLowerCase().startsWith(prefix.toLowerCase()) && !PLACEHOLDER.test(l) && l.length > prefix.length
    );
  }
  // Leitsatz: erst „ergänzt“, wenn hinter dem Doppelpunkt etwas anderes als der Platzhalter steht.
  const prefix = new RegExp(`^\\s*${escapeRegExp(item.label)}\\s*:\\s*(\\S.*)$`, 'i');
  return extrasLines(extras).some(l => {
    const rest = prefix.exec(l)?.[1];
    return rest !== undefined && !PLACEHOLDER.test(rest);
  });
};

export interface ExtrasInsertion {
  text: string;
  /** Der neu eingefügte Bereich (Zeichenindizes in `text`), zum kurzen Hervorheben. */
  start: number;
  end: number;
  /**
   * Was im Feld markiert werden soll: der erste „[…]“-Platzhalter des eingefügten Textes (der Nutzer
   * ersetzt ihn durch Tippen), sonst der Cursor am Ende des eingefügten Textes (Leitsatz „Sprache: “).
   */
  select: { start: number; end: number };
}

/** Fügt Vorschläge als eigene Zeilen in das Ergänzungsfeld ein (schon vorhandene werden nicht doppelt eingefügt). */
export const insertIntoExtras = (extras: string, adds: string[]): ExtrasInsertion => {
  const base = extras.replace(/\s+$/, '');
  // Schon vorhandene Zeilen nicht doppelt einfügen; ein Platzhalter-Vorschlag auch nicht, wenn der Nutzer die Zeile schon ausgefüllt hat.
  const fresh = adds
    .map(a => a.trim())
    .filter(a => a && !base.includes(a) && !(PLACEHOLDER.test(a) && hasLineWithPrefix(base, placeholderPrefix(a))));
  if (fresh.length === 0) {
    return { text: extras, start: extras.length, end: extras.length, select: { start: extras.length, end: extras.length } };
  }
  const prefix = base ? `${base}\n` : '';
  const added = fresh.join('\n');
  const end = prefix.length + added.length;
  const placeholder = PLACEHOLDER.exec(added);
  const select = placeholder
    ? { start: prefix.length + placeholder.index, end: prefix.length + placeholder.index + placeholder[0].length }
    : // Leitsatz: Cursor hinter „Doppelpunkt + Leerzeichen“
      { start: end, end };
  const text = prefix + added + (placeholder ? '' : /:$/.test(added) ? ' ' : '');
  return { text, start: prefix.length, end, select: placeholder ? select : { start: text.length, end: text.length } };
};

/** Einfache Prüfung der Link-Eingabe im Modal: http(s)://host.tld[/…] ohne Leerzeichen. */
export const isValidAdUrl = (value: string): boolean => {
  const url = value.trim();
  if (!/^https?:\/\/\S+$/i.test(url)) return false;
  try {
    const { hostname } = new URL(url);
    return /^[^.\s]+(\.[^.\s]+)+$/.test(hostname);
  } catch {
    return false;
  }
};

const SINGLE_URL = /^https?:\/\/\S+$/i;

/** Besteht der ganze Text nur aus einem Link? */
export const isSingleUrl = (text: string): boolean => SINGLE_URL.test(text.trim());

/** Kampagnenname darf vorgeschlagen werden, solange der Nutzer ihn nicht selbst geändert hat. */
export const mayReplaceName = (current: string, lastSuggestion: string): boolean =>
  current.trim() === '' || current.trim() === lastSuggestion.trim();

/** Mindestlänge des Briefings für Prüfung/Ableitung. */
export const MIN_DERIVE_LENGTH = 20;

const SUGGESTED_NAME_KEY = 'wizSuggestedCampaignName';

/**
 * Zuletzt vorgeschlagener Kampagnenname (pro Browser-Tab). Er überlebt ein Neuladen, damit ein
 * Vorschlag auch danach ersetzt werden darf, ein vom Nutzer selbst gesetzter Name aber nie.
 */
export const readSuggestedName = (): string => {
  try {
    return window.sessionStorage.getItem(SUGGESTED_NAME_KEY) ?? '';
  } catch {
    return '';
  }
};

export const rememberSuggestedName = (name: string): void => {
  try {
    if (name) window.sessionStorage.setItem(SUGGESTED_NAME_KEY, name);
    else window.sessionStorage.removeItem(SUGGESTED_NAME_KEY);
  } catch {
    // Ohne Speicher gilt: nach einem Neuladen wird nur ein leerer Name vorgeschlagen.
  }
};

/** Jobtitel darf neu abgeleitet werden, solange er leer oder der zuletzt vorgeschlagene ist. */
export const mayReplaceJobTitle = (current: string, lastSuggestion: string): boolean =>
  mayReplaceName(current, lastSuggestion);

const SUGGESTED_JOB_TITLE_KEY = 'wizSuggestedJobTitle';

/** Zuletzt vorgeschlagener Jobtitel (pro Browser-Tab), analog zum Kampagnennamen. */
export const readSuggestedJobTitle = (): string => {
  try {
    return window.sessionStorage.getItem(SUGGESTED_JOB_TITLE_KEY) ?? '';
  } catch {
    return '';
  }
};

export const rememberSuggestedJobTitle = (title: string): void => {
  try {
    if (title) window.sessionStorage.setItem(SUGGESTED_JOB_TITLE_KEY, title);
    else window.sessionStorage.removeItem(SUGGESTED_JOB_TITLE_KEY);
  } catch {
    // Ohne Speicher gilt: nach einem Neuladen wird nur ein leerer Titel vorgeschlagen.
  }
};
